import React, { useEffect, useState, useRef } from "react";
import { MdBusiness, MdAdd } from "react-icons/md";
import axios from "../../../utils/api/axios";
import toast from "react-hot-toast";

export type Company = {
  company_id: number;
  company_name: string;
  company_logo?: string | null;
};

type CompanySearchDropdownProps = {
  value: string;
  onChange: (companyName: string, companyId?: number, companyLogo?: string | null) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
};

const CompanySearchDropdown: React.FC<CompanySearchDropdownProps> = ({
  value,
  onChange,
  placeholder = "Search or add your company...",
  className = "",
  error = false
}) => {
  const [searchQuery, setSearchQuery] = useState(value);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Search for companies when query changes
  useEffect(() => {
    const searchCompanies = async () => {
      if (!searchQuery || searchQuery.trim() === "") {
        setCompanies([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axios.get("/v2/companies/search", {
          params: { query: searchQuery }
        });
        setCompanies(response.data || []);
      } catch (error) {
        console.error("Error searching companies:", error);
        setCompanies([]);
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(searchCompanies, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Update search query when value prop changes
  useEffect(() => {
    setSearchQuery(value);
  }, [value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    setIsOpen(true);
    setSelectedIndex(0);
    onChange(newValue, undefined, null); // Update parent with the text value
  };

  const handleSelectCompany = (company: Company) => {
    setSearchQuery(company.company_name);
    onChange(company.company_name, company.company_id, company.company_logo);
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    if (!searchQuery || searchQuery.trim() === "") {
      toast.error("Please enter a company name");
      return;
    }
    // Just pass the company name to parent, backend will create it
    onChange(searchQuery.trim(), undefined, null);
    setIsOpen(false);
    toast.success(`"${searchQuery.trim()}" will be added as a new company`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    const totalOptions = companies.length + 1; // +1 for "Create new" option

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => (prev < totalOptions - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex < companies.length) {
        handleSelectCompany(companies[selectedIndex]);
      } else {
        handleCreateNew();
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const showDropdown = isOpen && (companies.length > 0 || searchQuery.trim() !== "");

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <div className="relative">
        <MdBusiness
          className={`absolute left-3 top-1/2 -translate-y-1/2 text-lg ${
            error ? "text-red-500" : "text-gray-400"
          }`}
        />
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`w-full rounded-md border ${
            error
              ? "border-red-300 focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          } bg-white px-4 py-3.5 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-opacity-20`}
        />
      </div>

      {showDropdown && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-gray-300 bg-white shadow-lg">
          {isLoading ? (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-600">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
              <span>Searching...</span>
            </div>
          ) : (
            <>
              {companies.length > 0 && (
                <div className="max-h-60 overflow-y-auto">
                  {companies.map((company, index) => (
                    <div
                      key={company.company_id}
                      onClick={() => handleSelectCompany(company)}
                      className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        selectedIndex === index
                          ? "bg-blue-50 text-blue-900"
                          : "hover:bg-gray-50 text-gray-900"
                      }`}
                    >
                      <MdBusiness className={`text-lg ${
                        selectedIndex === index ? "text-blue-600" : "text-gray-400"
                      }`} />
                      <span>{company.company_name}</span>
                    </div>
                  ))}
                </div>
              )}

              {searchQuery.trim() !== "" && (
                <>
                  {companies.length > 0 && (
                    <div className="border-t border-gray-200" />
                  )}
                  <div
                    onClick={handleCreateNew}
                    className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                      selectedIndex === companies.length
                        ? "bg-green-50 text-green-900"
                        : "hover:bg-gray-50 text-gray-900"
                    }`}
                  >
                    <MdAdd className={`text-lg ${
                      selectedIndex === companies.length ? "text-green-600" : "text-gray-400"
                    }`} />
                    <div>
                      <div className="font-medium">Add "{searchQuery.trim()}"</div>
                      <div className="text-xs text-gray-500">Create new company</div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanySearchDropdown;

